import type { Equipment } from "@tiny-seas/shared";
import type { CharacterPose } from "./character-pose.ts";

/**
 * How a character's arms are layered. Babylon-free; `scene/actors.ts` applies it.
 *
 * 1. View poses. Tool grips, cuts, aiming and station work are authored in the action frame:
 *    camera-local in first person (the viewmodel), the head-pitched chest in third person.
 *    They are laid out for a camera at the eye, so the hands sit well out in front.
 * 2. Third-person carry. At rest (`carryWeight`), the third-person action frame drops and
 *    pulls in by the equipment's `THIRD_PERSON_CARRY` offset, the tool hand turns by its yaw,
 *    and the frame follows only part of the look pitch: a held tool rides a bent-elbow carry
 *    instead of the viewmodel's straight-armed reach.
 *    Cutting, guarding, aiming, reloading and working restore the authored reach.
 * 3. Free hands (third person). A hand that holds nothing hangs from its shoulder in the
 *    gravity (body-yaw) frame (`freeHandToRef`), swinging with the opposite leg (`armSwing`).
 * 4. Held hands sway with the same cadence (`Gait.carrySway`); sprinting pumps every hand.
 * 5. First-person lag. The viewmodel trails the camera's motion relative to the body (head
 *    bob, sway, landing dip, crouch) on a spring (`ViewmodelLag`), so the hands carry weight
 *    instead of sitting glued to the lens.
 *
 * Swing phases come from the rig's single gait clock (π per footfall), the same one the
 * feet use, so the arms can never drift out of step with the legs.
 */

/** Third-person action-frame origin on the torso: chest height, just ahead of the spine. */
export const THIRD_PERSON_ACTION_FRAME = { x: 0, y: 1.43, z: 0.05 } as const;

/**
 * Third-person carry per held tool: torso-frame drop (y) and draw-in (z) of the action frame,
 * and a turn (yaw, radians, about the vertical through the grip) of the tool hand. Only tools
 * whose view poses reach out at arm's length need one: the shovel already rests on the
 * shoulder, the spyglass and bucket are placed on the body directly. The cutlass turns its
 * blade out of the face it leans across for the view.
 */
export const THIRD_PERSON_CARRY: Readonly<Record<Equipment, { readonly y: number; readonly z: number; readonly yaw: number }>> = {
  sword: { y: -0.24, z: -0.28, yaw: 0.45 },
  pistol: { y: -0.20, z: -0.18, yaw: 0 },
  shovel: { y: 0, z: 0, yaw: 0 },
  spyglass: { y: 0, z: 0, yaw: 0 },
  bucket: { y: 0, z: 0, yaw: 0 },
};

/** Share of the look pitch a resting third-person carry follows; aiming follows all of it. */
export const CARRY_PITCH_SHARE = 0.3;

/**
 * 1 while the held tool is only being carried, 0 while it is used: cutting, guarding,
 * staggering, aiming, raising the spyglass, reloading, working, sprinting, stations.
 */
export function carryWeight(a: Readonly<CharacterPose>): number {
  if (a.action !== "idle" || a.station !== "" || a.sprinting || a.slideProgress >= 0) return 0;
  if (a.attackPhase >= 0 || a.blocking || a.parryRaise > 0 || a.staggerRemaining > 0 || a.pistolReloadProgress >= 0) return 0;
  return (1 - Math.max(0, Math.min(1, a.pistolAimBlend))) * (1 - Math.max(0, Math.min(1, a.scopeBlend)));
}

/**
 * Fore-aft swing of arm `arm` (0 left, 1 right) in −1…1, forward positive, on the rig's
 * gait clock (`walkPhase`, π per footfall). Foot `i` strikes at `walkPhase + iπ ≡ 0 (mod 2π)`;
 * each arm peaks forward as the opposite foot strikes.
 */
export function armSwing(walkPhase: number, arm: number): number {
  return Math.cos(walkPhase + (arm === 0 ? Math.PI : 0));
}

export interface FreeHand {
  /** Contact relative to the shoulder, body-yaw frame (+x right, +y up, +z forward). */
  x: number; y: number; z: number;
  /** Elbow pole relative to the shoulder, same frame. */
  poleX: number; poleY: number; poleZ: number;
  /** Palm normal and finger direction, same frame. */
  palmX: number; palmY: number; palmZ: number;
  fingersX: number; fingersY: number; fingersZ: number;
}

export function createFreeHand(): FreeHand {
  return { x: 0, y: 0, z: 0, poleX: 0, poleY: 0, poleZ: 0, palmX: 0, palmY: 0, palmZ: 0, fingersX: 0, fingersY: 0, fingersZ: 0 };
}

/**
 * A hand that holds nothing, hanging from its shoulder with a slightly bent elbow, palm to
 * the thigh. `swing` is the signed fore-aft travel in metres (`armSwing` × gait amplitude);
 * the hand rises on a pendulum arc and turns in across the body as it comes forward.
 * Crouched, the forearms come forward over the knees instead of hanging into the shins.
 */
export function freeHandToRef(side: number, swing: number, crouched: boolean, out: FreeHand): FreeHand {
  const forward = Math.max(0, swing);
  out.x = side * (crouched ? 0.07 : 0.05) - side * 0.25 * forward;
  out.y = (crouched ? -0.44 : -0.57) + 0.9 * swing * swing;
  out.z = (crouched ? 0.20 : 0.05) + swing;
  // The elbow trails behind the shoulder–hand line, a little outboard.
  out.poleX = side * 0.12; out.poleY = -0.30; out.poleZ = -0.35;
  out.palmX = -side; out.palmY = 0; out.palmZ = crouched ? -0.2 : 0;
  // Fingers continue the forearm: down, swinging forward with the hand.
  out.fingersX = 0; out.fingersY = crouched ? -0.75 : -1; out.fingersZ = (crouched ? 0.7 : 0.2) + 2 * swing;
  return out;
}

/** Most the first-person viewmodel may trail the camera on any axis (m). */
export const VIEWMODEL_LAG_LIMIT = 0.035;
/** Natural frequency of the viewmodel spring (rad/s): about a third of a footfall's head bob shows. */
const VIEWMODEL_LAG_OMEGA = 26;
/** Camera jumps larger than this relative to the body (teleport, station hop) re-seat the hands (m). */
const VIEWMODEL_LAG_JUMP = 0.3;
const VIEWMODEL_LAG_SUBSTEP = 1 / 240;

/**
 * Critically damped first-person hand lag. Each update takes the camera's motion relative to
 * the body expressed in camera axes; the hands are a mass sprung to the camera, so they trail
 * its bob and dips and settle back onto it. The motion is spread over fixed substeps, so the
 * trail is the same at any frame rate; at `VIEWMODEL_LAG_LIMIT` the camera drags the hands along.
 */
export class ViewmodelLag {
  /** Hand offset from the camera-held pose, camera axes (m). */
  readonly offset: [number, number, number] = [0, 0, 0];
  /** Hand velocity relative to the body, camera axes (m/s). */
  private readonly velocity: [number, number, number] = [0, 0, 0];

  reset(): void { this.offset.fill(0); this.velocity.fill(0); }

  /** `dx, dy, dz`: camera displacement relative to the body since the last update, camera axes. */
  update(dx: number, dy: number, dz: number, dt: number): void {
    if (Math.hypot(dx, dy, dz) > VIEWMODEL_LAG_JUMP) { this.reset(); return; }
    const w = VIEWMODEL_LAG_OMEGA, limit = VIEWMODEL_LAG_LIMIT;
    const steps = Math.max(1, Math.min(64, Math.ceil(Math.max(0, dt) / VIEWMODEL_LAG_SUBSTEP)));
    const h = Math.max(0, dt) / steps;
    for (let axis = 0; axis < 3; axis++) {
      const step = (axis === 0 ? dx : axis === 1 ? dy : dz) / steps;
      // The camera moves linearly through the frame; with no elapsed time the hands just keep their place.
      const camera = h > 0 ? step / h : 0;
      let x = this.offset[axis]!, v = this.velocity[axis]!;
      for (let i = 0; i < steps; i++) {
        x -= step;
        if (Math.abs(x) >= limit) { x = Math.sign(x) * limit; v = camera; }
        // Semi-implicit Euler; damping acts on the hands' velocity relative to the camera.
        v += (-w * w * x - 2 * w * (v - camera)) * h;
        x += v * h;
      }
      this.offset[axis] = Math.max(-limit, Math.min(limit, x)); this.velocity[axis] = v;
    }
  }
}
